import {AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AreaChartComponent, AreaChartModule} from '@swimlane/ngx-charts';
import {map, Observable} from 'rxjs';
import {ApiWeatherForecastHourResponseDto, Weather} from '@birco/rainmesh-api';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {curveNatural} from 'd3-shape';
import {NgForOf} from '@angular/common';
import {WeatherService} from '../../../../core/services/weather.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LocalizedNumberPipe} from '../../../../shared/pipes/localized-number.pipe';
import {LocaleService} from '../../../../translation/services/locale.service';

type TemperatureChartPoint = {
  name: Date;
  value: number;
  icon?: string;
  precipitationChance: number;
};

@Component({
  selector: 'app-chart-temperature',
  imports: [
    AreaChartModule,
    NgForOf,
    TranslatePipe,
    LocalizedNumberPipe,
  ],
  templateUrl: './chart-temperature.component.html',
  styleUrl: './chart-temperature.component.scss'
})
export class ChartTemperatureComponent implements OnInit, AfterViewInit, OnDestroy {
  private _weatherService = inject(WeatherService);
  private destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private static readonly timeSuffixKey = 'default.timeSuffix';

  @ViewChild('chartRef') chartRef!: AreaChartComponent;
  @ViewChild('container', {static: true}) containerRef!: ElementRef<HTMLDivElement>;
  private resizeObserver!: ResizeObserver;

  @Input() hourlyForecast$?: Observable<ApiWeatherForecastHourResponseDto[]>
  chartData: { name: string, series: TemperatureChartPoint[] }[] = []

  xScale: any;
  yScale: any;
  xMin!: Date;
  xMax!: Date;
  width = 0;
  height = 0;

  curve = curveNatural;
  customColors = [
    {name: 'Temperatur', value: '#E25822'}
  ];

  ngOnInit() {
    this.hourlyForecast$?.pipe(
      map((weatherArray) => [{
        name: 'Temperatur',
        series: weatherArray
          .map(w => ({
            name: new Date(w.dateTime),
            value: Math.round(w.temperature),
            icon: this.getWeatherIcon(w.weather),
            precipitationChance: Math.round(w.precipitationChance * 100),
          }))
      }]),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.chartData = [...data];
    });
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => {
      setTimeout(() => this.updateDimensionsAndScales(), 500);
    });
    this.resizeObserver.observe(this.containerRef.nativeElement);
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  updateDimensionsAndScales() {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;

    const series = this.chartData[0].series;
    this.xMin = new Date(series[0].name);
    this.xMax = new Date(series[series.length - 1].name);

    if (this.chartRef) {
      this.xScale = this.chartRef.xScale;
      this.yScale = this.chartRef.yScale;
    }
  }

  getWeatherIcon(weather: Weather): string {
    return this._weatherService.utils.getWeatherIcon(weather)
  }

  get min() {
    const values = this.chartData[0] ? this.chartData[0].series.map(s => s.value) : [0];
    const minValue = Math.min(...values);
    return minValue > 0 ? 0 : minValue - 3;
  }

  get max() {
    const values = this.chartData[0] ? this.chartData[0].series.map(s => s.value) : [0];
    const maxValue = Math.max(...values);
    return maxValue + 5;
  }

  formatTemp(value: number) {
    return `${Math.round(value)}℃`
  }

  formatTime = (value: Date) => `${value.toLocaleTimeString(this.localeService.userLanguage, {hour: '2-digit', minute: '2-digit'})}${this.getTimeSuffix()}`

  getTooltipPoint(model: TemperatureChartPoint | TemperatureChartPoint[] | undefined): TemperatureChartPoint | null {
    if (Array.isArray(model)) {
      return model[0] ?? null;
    }

    return model ?? null;
  }

  formatTooltipDate(point: TemperatureChartPoint): string {
    return this.formatTime(point.name);
  }

  private getTimeSuffix(): string {
    const suffix = this.translateService.instant(ChartTemperatureComponent.timeSuffixKey);
    return suffix ? ` ${suffix}` : '';
  }
}
